import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { loginAdmin, checkAdminSession, requestForgotPassword } from '@/lib/auth';

export default function AdminLoginPage() {
  const [email, setEmail] = useState('nabilasundas6@gmail.com');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Forgot password modal state
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('nabilasundas6@gmail.com');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg, setForgotMsg] = useState('');
  
  const navigate = useNavigate();

  // If already authenticated, redirect directly to dashboard
  useEffect(() => {
    checkAdminSession().then((user) => {
      if (user) {
        navigate('/admin/dashboard', { replace: true });
      }
    });
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      setError('Please fill in all required fields.');
      return;
    }

    setLoading(true);

    const result = await loginAdmin(trimmedEmail, password);

    if (result.success) {
      navigate('/admin/dashboard', { replace: true });
    } else {
      setError(result.error || 'Invalid login credentials. Please try again.');
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMsg('');

    const res = await requestForgotPassword(forgotEmail);
    setForgotMsg(res.message);
    setForgotLoading(false);
  };

  return (
    <div className="min-h-screen bg-ink-950 text-white flex items-center justify-center px-4 py-12 relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(139,92,246,0.15),transparent_60%)] pointer-events-none" />

      <div className="relative w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-gradient-to-br from-neon-600 to-neon-400 text-white text-2xl font-bold mb-4 shadow-[0_0_30px_rgba(139,92,246,0.4)]">
            FC
          </div>
          <h1 className="font-display text-3xl font-bold text-gradient">Admin Login</h1>
          <p className="mt-2 text-sm text-white/50">FormaCode Security Portal</p>
        </div>

        <form onSubmit={handleSubmit} className="glass rounded-2xl p-6 sm:p-8 space-y-5 border border-white/10 shadow-2xl">
          {error && (
            <div className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-start gap-2">
              <i className="ri-error-warning-line text-lg shrink-0 mt-0.5" />
              <span className="leading-snug">{error}</span>
            </div>
          )}
          {success && (
            <div className="px-4 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-sm flex items-start gap-2">
              <i className="ri-checkbox-circle-line text-lg shrink-0 mt-0.5" />
              <span>{success}</span>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-white/70 mb-2">Email Address *</label>
            <div className="relative">
              <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40 text-sm" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full pl-10 pr-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-neon-500 outline-none transition"
                placeholder="nabilasundas6@gmail.com"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-semibold text-white/70">Password *</label>
              <button
                type="button"
                onClick={() => { setShowForgotModal(true); setForgotMsg(''); }}
                className="text-xs text-neon-400 hover:text-neon-300 transition"
              >
                Forgot Password?
              </button>
            </div>
            <div className="relative">
              <i className="ri-lock-password-line absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40 text-sm" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full pl-10 pr-12 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-neon-500 outline-none transition"
                placeholder="Enter password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white p-1 transition"
                title={showPassword ? 'Hide Password' : 'Show Password'}
              >
                <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full text-white font-semibold px-8 py-3.5 rounded-lg text-sm whitespace-nowrap inline-flex items-center justify-center gap-2 transition disabled:opacity-50"
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                Authenticating...
              </>
            ) : (
              <>
                Sign In to Dashboard
                <i className="ri-login-box-line" />
              </>
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-white/50 hover:text-neon-300 transition inline-flex items-center gap-1.5">
            <i className="ri-arrow-left-line" /> Back to Website
          </Link>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm transition-opacity"
            onClick={() => setShowForgotModal(false)}
          />
          <div className="relative glass rounded-2xl p-6 sm:p-8 max-w-md w-full border border-white/10 z-10 space-y-4 shadow-2xl">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-xl text-white">Reset Password</h3>
              <button
                onClick={() => setShowForgotModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 text-white/60 hover:text-white"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <p className="text-sm text-white/60 leading-relaxed">
              Enter your registered admin email address. A single-use secure reset link will be sent to your email.
            </p>

            {forgotMsg && (
              <div className="px-4 py-3 rounded-lg bg-neon-500/10 border border-neon-500/20 text-neon-300 text-sm flex items-start gap-2">
                <i className="ri-information-line text-lg shrink-0 mt-0.5" />
                <span>{forgotMsg}</span>
              </div>
            )}

            <form onSubmit={handleForgotSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-white/70 mb-2">Admin Email *</label>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-neon-500 outline-none transition"
                  placeholder="nabilasundas6@gmail.com"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForgotModal(false)}
                  className="flex-1 py-3 rounded-lg border border-white/10 text-white/70 hover:text-white hover:bg-white/5 text-sm font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={forgotLoading}
                  className="flex-1 btn-primary py-3 rounded-lg text-white text-sm font-semibold transition disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                  {forgotLoading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}